import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { productService } from '../lib/firebaseService';
import { ArrowLeft, Upload, Users, DollarSign, TrendingUp, Trash2 } from 'lucide-react';
import '../styles/components-extended.css';
import { format } from 'date-fns';

import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export default function EventDetail() {
    const { id } = useParams();
    const { data: event, isLoading: eventLoading } = useQuery({
        queryKey: ['event', id],
        queryFn: () => productService.getEvent(id)
    });

    const navigate = useNavigate();

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this event? This action cannot be undone.')) {
            await productService.deleteEvent(id);
            navigate('/events');
        }
    };

    // Fetch Sales Data for this Event
    const { data: sales, isLoading: salesLoading } = useQuery({
        queryKey: ['event-sales', id],
        queryFn: () => productService.getSalesByEvent(id),
        enabled: !!event
    });

    if (eventLoading) return <div>Loading...</div>;
    if (!event) return <div>Event not found</div>;

    // Process Sales Data for Charts
    const processData = () => {
        if (!sales || sales.length === 0) return null;

        // 1. Hourly Traffic (Peak Hours)
        const hours = {};
        for (let i = 0; i < 24; i++) hours[i] = 0; // Init all hours

        sales.forEach(item => {
            const date = new Date(item.timestamp);
            const hour = date.getHours();
            // If date is invalid, it defaults to now, so be careful. 
            // We assume valid timestamps from ImportWizard.
            if (!isNaN(hour)) {
                hours[hour] += item.quantity; // Tracking volume, or use item.price for revenue
            }
        });

        const hourlyData = Object.keys(hours).map(h => ({
            hour: `${h}:00`,
            volume: hours[h]
        }));

        // 2. Top Products
        const products = {};
        sales.forEach(item => {
            if (products[item.productName]) {
                products[item.productName] += item.quantity;
            } else {
                products[item.productName] = item.quantity;
            }
        });

        const topProducts = Object.keys(products)
            .map(name => ({ name, qty: products[name] }))
            .sort((a, b) => b.qty - a.qty)
            .slice(0, 5);

        return { hourlyData, topProducts };
    };

    const analytics = processData();

    // Calculate Total Revenue from Sales dynamically
    const totalRevenue = sales ? sales.reduce((acc, item) => {
        const price = Number(item.price) || 0;
        const qty = Number(item.quantity) || 0;
        return acc + (price * qty);
    }, 0) : (event.revenue || 0);

    return (
        <div className="max-w-[1200px] mx-auto">
            <div className="mb-6">
                <Link to="/events" className="text-gray-500 hover:text-black flex items-center text-sm mb-4">
                    <ArrowLeft size={16} className="mr-1" /> Back to Events
                </Link>
                <div className="flex justify-between items-end">
                    <div>
                        <h1 className="text-3xl font-bold mb-2">{event.name}</h1>
                        <p className="text-gray-500 flex items-center gap-4">
                            <span>{format(new Date(event.date), 'EEEE, MMMM d, yyyy')}</span>
                            <span>•</span>
                            <span>{event.venue}</span>
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={handleDelete} className="btn bg-red-50 text-red-600 hover:bg-red-100 border-red-100">
                            <Trash2 size={16} className="mr-2" />
                            Delete Event
                        </button>
                        <Link to="/imports" className="btn btn-secondary">
                            <Upload size={16} className="mr-2" />
                            Import Sales Report
                        </Link>
                    </div>
                </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-medium text-gray-500">Revenue</h3>
                        <DollarSign size={18} className="text-gray-400" />
                    </div>
                    <p className="text-2xl font-bold">€{totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</p>
                </div>
                <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-medium text-gray-500">Guests</h3>
                        <Users size={18} className="text-gray-400" />
                    </div>
                    <p className="text-2xl font-bold">{event.guestCount}</p>
                </div>
                <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-medium text-gray-500">Avg Spend</h3>
                        <TrendingUp size={18} className="text-gray-400" />
                    </div>
                    <p className="text-2xl font-bold">
                        {event.guestCount > 0 ? `€${(totalRevenue / event.guestCount).toFixed(2)}` : '€0.00'}
                    </p>
                </div>
                <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-medium text-gray-500">Profit Margin</h3>
                        <div className="text-xs text-green-600 font-bold bg-green-50 px-2 py-1 rounded-full">+0%</div>
                    </div>
                    <p className="text-2xl font-bold">--%</p>
                    <p className="text-xs text-gray-400 mt-1">Needs Sales Import</p>
                </div>
            </div>

            {/* Analytics Section */}
            {analytics ? (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">

                    {/* Peak Hours Chart */}
                    <div className="lg:col-span-2 bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                        <h3 className="text-lg font-bold text-gray-900 mb-6">Peak Hours (Traffic)</h3>
                        <div className="h-[300px] w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={analytics.hourlyData}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="hour" axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 12 }} />
                                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#9CA3AF', fontSize: 12 }} />
                                    <Tooltip
                                        cursor={{ fill: '#F3E8FF' }}
                                        contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                    />
                                    <Bar dataKey="volume" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Top Products */}
                    <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                        <h3 className="text-lg font-bold text-gray-900 mb-4">Top Products</h3>
                        <div className="space-y-4">
                            {analytics.topProducts.map((p, i) => (
                                <div key={i} className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <div className="w-8 h-8 rounded-full bg-purple-50 text-purple-600 flex items-center justify-center font-bold text-xs mr-3">
                                            {i + 1}
                                        </div>
                                        <span className="font-medium text-gray-700">{p.name}</span>
                                    </div>
                                    <span className="font-bold text-gray-900">{p.qty}u</span>
                                </div>
                            ))}
                        </div>
                    </div>

                </div>
            ) : (
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm min-h-[300px] flex items-center justify-center">
                    <div className="text-center">
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Sales Data Yet</h3>
                        <p className="text-gray-500 max-w-md mx-auto mb-6">
                            Import a SumUp CSV report to see detailed sales breakdowns, product popularity, and financial analysis.
                        </p>
                        <Link to="/imports" className="btn btn-primary">
                            Import Sales Data
                        </Link>
                    </div>
                </div>
            )}
        </div>
    );
}
